import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, useScroll, useMotionValueEvent } from "framer-motion";
import { ShoppingBag } from "lucide-react";
import { Logo } from "./Logo";
import { useLang } from "../lib/i18n";
import { useBag } from "../lib/api";

const LangToggle = () => {
  const { lang, setLang } = useLang();
  return (
    <div className="f-mono text-[12px] flex items-center gap-1">
      {["tr", "en"].map((l, i) => (
        <span key={l} className="flex items-center gap-1">
          {i > 0 && <span className="opacity-50">/</span>}
          <button
            data-testid={`language-toggle-${l}`}
            onClick={() => setLang(l)}
            className={`uppercase transition-opacity duration-300 ${lang === l ? "opacity-100 underline underline-offset-4" : "opacity-60 hover:opacity-100"}`}
          >
            {l}
          </button>
        </span>
      ))}
    </div>
  );
};

const NavLink = ({ to, label, testid, active }) => (
  <Link to={to} data-testid={testid} className="relative group f-mono text-[12px] uppercase tracking-[0.2em]">
    {label}
    <span className={`absolute left-0 -bottom-1 h-px bg-current transition-[width] duration-500 ${active ? "w-full" : "w-0 group-hover:w-full"}`} />
  </Link>
);

export const Header = () => {
  const { t } = useLang();
  const { count } = useBag();
  const { pathname } = useLocation();
  const { scrollY } = useScroll();
  const [hidden, setHidden] = useState(false);
  useMotionValueEvent(scrollY, "change", (v) => {
    const prev = scrollY.getPrevious() ?? 0;
    setHidden(v > 120 && v > prev);
  });
  return (
    <motion.header
      data-testid="site-header"
      animate={{ y: hidden ? "-110%" : "0%" }}
      transition={{ duration: 0.5, ease: [0.19, 1, 0.22, 1] }}
      className="fixed top-0 inset-x-0 z-50 text-white mix-blend-difference pointer-events-none"
    >
      <div className="relative flex items-center justify-between px-5 md:px-10 py-5 pointer-events-auto">
        <Logo />
        <nav className="hidden md:grid grid-cols-2 gap-x-14 items-center absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
          <span className="justify-self-end"><NavLink to="/collections/streetwear" label={t("street")} testid="nav-link-streetwear" active={pathname.includes("streetwear")} /></span>
          <span className="justify-self-start"><NavLink to="/collections/old-money" label={t("oldmoney")} testid="nav-link-oldmoney" active={pathname.includes("old-money")} /></span>
        </nav>
        <div className="flex items-center gap-5">
          <LangToggle />
          <span data-testid="bag-count" className="flex items-center gap-1.5 f-mono text-[12px]">
            <ShoppingBag size={15} strokeWidth={1.5} /> {count}
          </span>
        </div>
      </div>
      <nav className="md:hidden grid grid-cols-2 gap-x-10 pb-2 pointer-events-auto">
        <span className="justify-self-end"><NavLink to="/collections/streetwear" label={t("street")} testid="nav-link-streetwear-mobile" active={pathname.includes("streetwear")} /></span>
        <span className="justify-self-start"><NavLink to="/collections/old-money" label={t("oldmoney")} testid="nav-link-oldmoney-mobile" active={pathname.includes("old-money")} /></span>
      </nav>
    </motion.header>
  );
};
